import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../hooks/useAuth';
import { DeviceType, RegisteredDevice } from '../types';

// ── Device ID ─────────────────────────────────────────────────────────────
const DEVICE_ID_KEY = 'aspis_device_id';

function getOrCreateDeviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
}

// ── Context Types ─────────────────────────────────────────────────────────
interface DeviceContextType {
    deviceId: string;
    deviceType: DeviceType;
    deviceLabel: string;
    isRegistered: boolean;
    /** True while the initial Firestore registration check is in progress. */
    isChecking: boolean;
    registerDevice: (
        type: DeviceType,
        label: string,
        registeredBy: string,
        registeredByName: string,
        tenantId: string
    ) => Promise<void>;
    deregisterDevice: () => Promise<void>;
}

const DeviceContext = createContext<DeviceContextType>({
    deviceId: '',
    deviceType: 'byod',
    deviceLabel: 'Personal Device',
    isRegistered: false,
    isChecking: false,
    registerDevice: async () => {},
    deregisterDevice: async () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────
export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [deviceId] = useState<string>(getOrCreateDeviceId);
    const [deviceType, setDeviceType] = useState<DeviceType>('byod');
    const [deviceLabel, setDeviceLabel] = useState('Personal Device');
    const [isRegistered, setIsRegistered] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    // Check Firestore registration on mount
    useEffect(() => {
        let cancelled = false;
        const checkRegistration = async () => {
            if (!user?.uid) {
                if (!cancelled) setIsChecking(false);
                return;
            }

            try {
                const snap = await getDoc(doc(db, 'registeredDevices', deviceId));
                if (!cancelled && snap.exists()) {
                    const data = snap.data() as RegisteredDevice;
                    setDeviceType(data.type);
                    setDeviceLabel(data.label);
                    setIsRegistered(true);
                    // Ping last active timestamp without blocking
                    setDoc(
                        doc(db, 'registeredDevices', deviceId),
                        { lastActiveAt: new Date().toISOString() },
                        { merge: true }
                    ).catch(() => {});
                }
            } catch (err) {
                console.error('[Device] Registration check failed:', err);
            } finally {
                if (!cancelled) setIsChecking(false);
            }
        };
        checkRegistration();
        return () => { cancelled = true; };
    }, [deviceId, user?.uid]);

    const registerDevice = useCallback(
        async (
            type: DeviceType,
            label: string,
            registeredBy: string,
            registeredByName: string,
            tenantId: string
        ) => {
            const device: RegisteredDevice = {
                deviceId,
                type,
                label,
                tenantId,
                registeredBy,
                registeredByName,
                registeredAt: new Date().toISOString(),
                lastActiveAt: new Date().toISOString(),
            };
            await setDoc(doc(db, 'registeredDevices', deviceId), device);
            setDeviceType(type);
            setDeviceLabel(label);
            setIsRegistered(true);
        },
        [deviceId]
    );

    const deregisterDevice = useCallback(async () => {
        await setDoc(
            doc(db, 'registeredDevices', deviceId),
            { type: 'byod', label: 'Deregistered', lastActiveAt: new Date().toISOString() },
            { merge: true }
        );
        setDeviceType('byod');
        setDeviceLabel('Personal Device');
        setIsRegistered(false);
    }, [deviceId]);

    return (
        <DeviceContext.Provider
            value={{ deviceId, deviceType, deviceLabel, isRegistered, isChecking, registerDevice, deregisterDevice }}
        >
            {children}
        </DeviceContext.Provider>
    );
};

export const useDevice = (): DeviceContextType => useContext(DeviceContext);
